import * as React from "react";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ChatLayout,
  Label,
  Skeleton,
  Switch,
  useToast,
} from "randmarcomps";
import { Handshake } from "lucide-react";
import { useParams } from "react-router-dom";
import {
  useGetV4PartnerByApplicationIdAccountManufacturerQuery,
  usePostV4PartnerByApplicationIdAccountManufacturerAndFieldNameMutation,
  useGetV4PartnerByRouteApplicationIdBusinessDevelopmentConversationsQuery,
} from "randmar-api-client";
import InlineSpinner from "../components/util/InlineSpinner";

function getErrorMessage(error: unknown) {
  if (!error) return "Please try again.";

  if (typeof error === "object" && "data" in (error as Record<string, unknown>)) {
    const data = (error as { data?: Record<string, unknown> }).data;
    if (data) {
      if (typeof data === "string") {
        return data;
      }
      if ("message" in data && typeof data.message === "string") {
        return data.message;
      }
      if ("title" in data && typeof data.title === "string") {
        return data.title;
      }
      if ("detail" in data && typeof data.detail === "string") {
        return data.detail;
      }
    }
  }

  if (typeof error === "object" && error && "message" in error && typeof error.message === "string") {
    return error.message;
  }

  return "Please try again.";
}

interface OpenToWorkToggleCardProps {
  applicationId?: string;
  className?: string;
}

function OpenToWorkToggleCard({ applicationId, className }: OpenToWorkToggleCardProps) {
  const { toast } = useToast();
  const { data: manufacturer, isLoading, isFetching, isError, refetch, error } =
    useGetV4PartnerByApplicationIdAccountManufacturerQuery(
      {
        applicationId: applicationId ?? "",
        withSpecification: false,
      },
    {
      skip: !applicationId,
    }
  );
  const [updateOpenToWork, { isLoading: isSaving }] =
    usePostV4PartnerByApplicationIdAccountManufacturerAndFieldNameMutation();
  const [pendingValue, setPendingValue] = React.useState<boolean | null>(null);

  const containerClasses = ["h-full w-full", className].filter(Boolean).join(" ");

  if (!applicationId) {
    return (
      <section className={containerClasses}>
        <Alert variant="destructive" className="rounded-3xl border border-red-100 bg-white">
          <AlertTitle>Missing application</AlertTitle>
          <AlertDescription>We couldn’t identify which manufacturer you’re managing.</AlertDescription>
        </Alert>
      </section>
    );
  }

  const isInitialLoading = isLoading && !manufacturer;

  if (isInitialLoading) {
    return (
      <section className={containerClasses}>
        <Skeleton className="h-36 w-full rounded-3xl" />
      </section>
    );
  }

  if (isError && !manufacturer) {
    return (
      <section className={containerClasses}>
        <Alert variant="destructive" className="rounded-3xl border border-red-100 bg-white">
          <AlertTitle>Can’t load chat availability</AlertTitle>
          <AlertDescription className="space-y-3">
            <p>Something went wrong while loading your Open to Work status.</p>
            <Button size="sm" variant="outline" onClick={() => refetch()}>
              Try again
            </Button>
          </AlertDescription>
        </Alert>
      </section>
    );
  }

  const currentStatus = pendingValue ?? manufacturer?.OpenToWork ?? false;
  const isBackgroundFetching = isFetching && !isInitialLoading;
  const statusStyle = currentStatus
    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
    : "border-slate-200 bg-slate-100 text-slate-600";
  const statusDotStyle = currentStatus ? "bg-emerald-500" : "bg-slate-400";
  const statusCopy = currentStatus
    ? "Resellers can see that you’re accepting introductions."
    : "Resellers won’t see you in Chat until you flip the switch back on.";

  const handleStatusChange = async (checked: boolean) => {
    if (!applicationId) {
      return;
    }

    setPendingValue(checked);
    try {
      await updateOpenToWork({
        applicationId,
        fieldName: "OpenToWork",
        newValue: String(checked),
      }).unwrap();

      toast({
        variant: "success",
        title: checked ? "Now open to reseller chats" : "Hiding from new chats",
        description: checked
          ? "Resellers can reach out instantly in the Chat tab."
          : "We’ll keep your brand hidden until you’re ready again.",
      });

      await refetch();
    } catch (updateError) {
      toast({
        variant: "destructive",
        title: "Couldn't update chat availability",
        description: getErrorMessage(updateError),
      });
    } finally {
      setPendingValue(null);
    }
  };

  return (
    <section className={containerClasses}>
      <Card className="flex h-full flex-col overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-sm">
        <CardHeader className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-4">
          <div className="rounded-2xl bg-indigo-50 p-3 text-indigo-600">
            <Handshake className="h-6 w-6" aria-hidden="true" />
          </div>
          <div>
            <CardTitle className="text-xl text-slate-900">Reseller chat availability</CardTitle>
            <CardDescription>Control whether resellers can introduce new opportunities.</CardDescription>
          </div>
        </div>
          <span
            className={`inline-flex w-full items-center justify-center gap-2 rounded-full border px-4 py-1.5 text-sm font-medium sm:w-auto sm:justify-start lg:justify-center ${statusStyle}`}
            aria-live="polite"
          >
          <span className={`h-2 w-2 rounded-full ${statusDotStyle}`} />
          {currentStatus ? "Open to Work" : "Not accepting intros"}
        </span>
        </CardHeader>
        <CardContent className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="space-y-1 text-sm text-slate-600">
          <p>{statusCopy}</p>
          <p className="text-xs text-slate-400">You can toggle this at any time—changes take effect instantly.</p>
          {isError && manufacturer && (
            <p className="text-xs font-medium text-amber-600">
              We couldn’t refresh the latest status, showing the last value on file.
            </p>
          )}
          {error && (
            <p className="text-xs text-slate-400">
              {getErrorMessage(error)}
            </p>
          )}
          </div>
          <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-100/70 bg-slate-50/60 px-4 py-3 sm:flex-nowrap sm:border-transparent sm:bg-transparent sm:px-0 sm:py-0">
            <Label htmlFor="open-to-work-toggle" className="text-sm font-medium text-slate-800">
              Open to Work
            </Label>
            <Switch
              id="open-to-work-toggle"
              checked={currentStatus}
              onCheckedChange={handleStatusChange}
              disabled={isSaving}
            />
            {(isSaving || isBackgroundFetching) && (
              <InlineSpinner size="sm" tone="muted" decorative />
            )}
          </div>
        </CardContent>
      </Card>
    </section>
  );
}

export default function Chat() {
  const { applicationId } = useParams<{ applicationId: string }>();
  const { data: conversations } =
    useGetV4PartnerByRouteApplicationIdBusinessDevelopmentConversationsQuery(
      {
        routeApplicationId: applicationId ?? "",
      },
      {
        skip: !applicationId,
      }
    );
  const shouldClampConversationList = (conversations?.length ?? 0) > 9;
  const chatLayoutClasses = [
    "randmar-chat-layout flex-1",
    shouldClampConversationList && "randmar-chat-layout--clamped-conversations",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <main className="flex min-h-[calc(100vh-8rem)] w-full flex-1 flex-col gap-6 rounded-[32px] bg-slate-50/80 p-4 shadow-inner sm:p-6 lg:p-8">
      <section className="flex w-full flex-1 flex-col gap-4">
        <OpenToWorkToggleCard applicationId={applicationId} className="w-full" />
        <section
          aria-label="Reseller chat"
          className="flex min-h-[480px] w-full flex-1 flex-col overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-sm sm:min-h-[540px] lg:min-h-[640px]"
        >
          <ChatLayout className={chatLayoutClasses} appID={applicationId ?? ""} />
        </section>
      </section>
    </main>
  );
}
